import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';

/**
 * The blue "chosen destination" identity, per colour scheme. The light tints
 * (#e3f2fd, #f5f5f5) glare on a dark paper and leave the text on them
 * unreadable, so dark mode uses translucent light-blue over whatever surface
 * the dialog sits on.
 */
const SELECTION_COLORS = {
  light: {
    fill: '#e3f2fd',
    fillHover: '#bbdefb',
    accent: '#1976d2',
    accentText: '#1565c0',
    rowHover: '#f5f5f5',
    action: '#1976d2',
    actionHover: '#1565c0',
    actionDisabled: '#e0e0e0',
    actionDisabledText: '#9e9e9e',
  },
  dark: {
    fill: 'rgba(100, 181, 246, 0.16)',
    fillHover: 'rgba(100, 181, 246, 0.26)',
    accent: '#64b5f6',
    accentText: '#90caf9',
    rowHover: 'rgba(255, 255, 255, 0.06)',
    action: '#3b9dff',
    actionHover: '#5cb0ff',
    actionDisabled: 'rgba(255, 255, 255, 0.12)',
    actionDisabledText: 'rgba(255, 255, 255, 0.3)',
  },
} as const;

/**
 * Represents a contract folder destination option for file moves
 */
export interface ContractFolderOption {
  id: number;
  partyA: string;
  partyB: string;
  contractTitle: string;
  contractDate: string | null;
  originalFolderId: number;
  path: string;
}

/**
 * Path node in the folder hierarchy
 */
export interface PathNode {
  id: number;
  name: string;
}

/**
 * Original path item returned by the original-paths API
 */
export interface OriginalPathItem {
  id: number; // The Originals folder ID to use in move API
  name: string; // Always "Originals"
  paths: PathNode[][]; // Array of path variations
}

/**
 * Processed folder option for display
 */
export interface FolderOption {
  id: number; // Originals folder ID
  displayName: string; // "Party A - Party B - Contract Title"
  partyA: string;
  partyB: string;
  contractTitle: string;
}

export interface MoveToFolderModalProps {
  open: boolean;
  onClose: () => void;
  onMove: (folderId: number) => void;
  selectedFileCount: number;
  originalPaths: OriginalPathItem[];
  isLoading?: boolean;
  isMoveInProgress?: boolean;
}

/**
 * MoveToFolderModal - Modal for moving files to a different contract folder
 *
 * In the Sorted section, only Original files can be moved.
 * This modal displays available Party A - Party B - Contract Title
 * combinations so users can select a destination folder.
 *
 * The file will be placed in the "Originals" folder under the selected contract.
 */
export const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  open,
  onClose,
  onMove,
  selectedFileCount,
  originalPaths,
  isLoading = false,
  isMoveInProgress = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const c = SELECTION_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light'];
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<FolderOption | null>(
    null
  );

  // Transform originalPaths to folder options with display names
  const folderOptions = useMemo<FolderOption[]>(() => {
    if (!originalPaths || originalPaths.length === 0) return [];

    return originalPaths.map((item) => {
      // Use the first path variation for display (Party A -> Party B -> Contract Title)
      const firstPath = item.paths[0] || [];
      const partyA = firstPath[0]?.name || '';
      const partyB = firstPath[1]?.name || '';
      const contractTitle = firstPath[2]?.name || '';

      // Create display name: "Party A - Party B - Contract Title"
      const displayName = [partyA, partyB, contractTitle]
        .filter(Boolean)
        .join(' - ');

      return {
        id: item.id, // Originals folder ID
        displayName,
        partyA,
        partyB,
        contractTitle,
      };
    });
  }, [originalPaths]);

  // Filter folders based on search term
  const filteredFolders = useMemo(() => {
    if (!searchTerm.trim()) return folderOptions;

    const lowerSearch = searchTerm.toLowerCase();
    return folderOptions.filter((folder) => {
      return folder.displayName.toLowerCase().includes(lowerSearch);
    });
  }, [folderOptions, searchTerm]);

  const handleMove = () => {
    if (selectedFolder) {
      onMove(selectedFolder.id);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedFolder(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DriveFileMoveIcon color="primary" />
          <Typography variant="h6">{t('Move to Folder')}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('Select a contract folder to move')} {selectedFileCount}{' '}
          {t('file')}
          {selectedFileCount !== 1 ? 's' : ''} {t('to')}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('Files will be moved to the')} <strong>{t('Originals')}</strong>{' '}
          {t(
            'folder of the selected contract. Party A, Party B, and Contract Title will be updated accordingly. File movement history will be maintained.'
          )}
        </Alert>

        {/* Search Input */}
        <TextField
          fullWidth
          placeholder={t('Search by Party A, Party B, Contract Title...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Selected Folder Preview */}
        {selectedFolder && (
          <Box
            sx={{
              mb: 2,
              py: 1,
              px: 1.5,
              bgcolor: c.fill,
              borderRadius: 1,
              border: '1px solid',
              borderColor: c.accent,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CheckCircleIcon sx={{ color: c.accent }} fontSize="small" />
            <Typography
              variant="body2"
              fontWeight="md"
              sx={{ color: c.accentText }}
            >
              {selectedFolder.displayName}
            </Typography>
          </Box>
        )}

        {/* Folder List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredFolders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchTerm
                ? 'No matching contract folders found'
                : 'No contract folders available'}
            </Typography>
          </Box>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              py: 0,
            }}
          >
            {filteredFolders.map((folder, index) => {
              const isSelected = selectedFolder?.id === folder.id;

              return (
                <React.Fragment key={folder.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => setSelectedFolder(folder)}
                      sx={{
                        py: 0.75,
                        px: 1.5,
                        '&.Mui-selected': {
                          bgcolor: c.fill,
                          borderLeft: `3px solid ${c.accent}`,
                          '&:hover': {
                            bgcolor: c.fillHover,
                          },
                        },
                        '&:hover': {
                          bgcolor: c.rowHover,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {isSelected ? (
                          <CheckCircleIcon
                            sx={{ color: c.accent }}
                            fontSize="small"
                          />
                        ) : (
                          <FolderIcon color="action" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 500 : 400,
                              color: isSelected ? c.accentText : 'text.primary',
                            }}
                          >
                            {folder.displayName}
                          </Typography>
                        }
                        sx={{ my: 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredFolders.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isMoveInProgress}
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleMove}
          variant="contained"
          disabled={!selectedFolder || isMoveInProgress}
          startIcon={
            isMoveInProgress ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <DriveFileMoveIcon />
            )
          }
          sx={{
            bgcolor: c.action,
            color: '#ffffff',
            '&:hover': {
              bgcolor: c.actionHover,
            },
            '&:disabled': {
              bgcolor: c.actionDisabled,
              color: c.actionDisabledText,
            },
          }}
        >
          {isMoveInProgress ? 'Moving...' : 'Move Files'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveToFolderModal;
